import { useRef } from 'react';
import { gsap, useGSAP, SplitText, EASE_DISPLAY, prefersReducedMotion } from './gsap';

type Options = {
  /** Delay in seconds before the first line starts. */
  delay?: number;
  /** Wait for the element to scroll into view instead of firing on mount. */
  onScroll?: boolean;
};

/**
 * Reveals a heading line by line, each line rising out of its own mask.
 *
 * The element must carry the `.split-target` class, which holds it invisible
 * until GSAP takes over — without that there is a flash of unstyled text
 * between first paint and hydration. Every exit path sets it visible again.
 *
 * The split waits on `document.fonts.ready`: splitting before the display face
 * lands measures the fallback font and breaks the lines in the wrong places.
 * GSAP's own `autoSplit` covers that case, but its asynchronous re-split races
 * StrictMode's double-mount cleanup and leaves the heading unsplit.
 */
export function useSplitReveal<T extends HTMLElement>({
  delay = 0,
  onScroll = false,
}: Options = {}) {
  const ref = useRef<T>(null);

  useGSAP(
    () => {
      const el = ref.current;
      if (!el) return;

      if (prefersReducedMotion()) {
        gsap.set(el, { autoAlpha: 1 });
        return;
      }

      let split: SplitText | undefined;
      let cancelled = false;

      document.fonts.ready.then(() => {
        if (cancelled) return;

        split = SplitText.create(el, {
          type: 'lines',
          mask: 'lines',
          linesClass: 'split-line',
        });

        gsap.set(el, { autoAlpha: 1 });
        gsap.from(split.lines, {
          yPercent: 115,
          duration: 1.05,
          ease: EASE_DISPLAY,
          stagger: 0.09,
          delay,
          scrollTrigger: onScroll ? { trigger: el, start: 'top 85%', once: true } : undefined,
        });
      });

      return () => {
        cancelled = true;
        split?.revert();
        // The tween is reverted by useGSAP's context, but the CSS hidden state
        // is not — put the element back to visible so a revert can never leave
        // the heading blank.
        gsap.set(el, { autoAlpha: 1 });
      };
    },
    { scope: ref, dependencies: [delay, onScroll] },
  );

  return ref;
}
